import React, { useState, useEffect, useRef } from 'react';
import { Card, Badge, ProgressBar, Button } from 'react-bootstrap';
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaRobot,
  FaGithub,
  FaBlog,
  FaVideo,
  FaPlay,
  FaPause,
  FaStop,
  FaClock,
} from 'react-icons/fa';
import PropTypes from 'prop-types';

import './ProjectCard.css';

const ProjectCard = ({
  project,
  onView,
  onEdit,
  onDelete,
  onTimerStart,
  onTimerPause,
  onTimerResume,
  onTimerStop,
}) => {
  const [timerActive, setTimerActive] = useState(project.timer_active || false);
  const [timerPaused, setTimerPaused] = useState(project.timer_paused || false);
  const [elapsedSeconds, setElapsedSeconds] = useState(project.elapsed_seconds || 0);
  const timerIntervalRef = useRef(null);

  // Timer effect
  useEffect(() => {
    if (timerActive && !timerPaused) {
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [timerActive, timerPaused]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleStartTimer = async (e) => {
    e.stopPropagation();
    setTimerActive(true);
    setTimerPaused(false);
    if (onTimerStart) {
      await onTimerStart(project.id);
    }
  };

  const handlePauseTimer = async (e) => {
    e.stopPropagation();
    setTimerPaused(true);
    if (onTimerPause) {
      await onTimerPause(project.id, elapsedSeconds);
    }
  };

  const handleResumeTimer = async (e) => {
    e.stopPropagation();
    setTimerPaused(false);
    if (onTimerResume) {
      await onTimerResume(project.id);
    }
  };

  const handleStopTimer = async (e) => {
    e.stopPropagation();
    setTimerActive(false);
    setTimerPaused(false);
    if (onTimerStop) {
      await onTimerStop(project.id, elapsedSeconds);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'not_started':
        return 'secondary';
      case 'archived':
        return 'dark';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'not_started':
        return 'Not Started';
      case 'archived':
        return 'Archived';
      default:
        return status;
    }
  };

  const getSharingIcon = (platform) => {
    switch (platform) {
      case 'github':
        return <FaGithub />;
      case 'blog':
        return <FaBlog />;
      case 'video':
        return <FaVideo />;
      default:
        return null;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onView(project);
    }
  };

  return (
    <Card className="project-card">
      <div
        className="project-thumbnail-container"
        onClick={() => onView(project)}
        onKeyDown={handleKeyPress}
        role="button"
        tabIndex={0}
        aria-label={`View ${project.title}`}
      >
        <div className="project-thumbnail-placeholder">
          <i className="fa-solid fa-diagram-project fa-3x" />
        </div>
        {project.ai_generated && (
          <Badge bg="primary" className="ai-badge" aria-label="AI project">
            <FaRobot />
          </Badge>
        )}
        {project.status === 'completed' && (
          <Badge bg="success" className="completion-badge">
            Completed
          </Badge>
        )}
        <div className="view-overlay">
          <FaEye size={32} />
        </div>
      </div>

      <Card.Body>
        <Card.Title className="project-title">{project.title}</Card.Title>

        {project.description && (
          <Card.Text className="project-description">
            {project.description.length > 100
              ? `${project.description.substring(0, 100)}...`
              : project.description}
          </Card.Text>
        )}

        <div className="project-status mb-2">
          <Badge bg={getStatusVariant(project.status)}>{getStatusLabel(project.status)}</Badge>
          {project.difficulty_level && (
            <Badge bg="info" className="ms-1">
              {project.difficulty_level}
            </Badge>
          )}
        </div>

        {project.technologies && project.technologies.length > 0 && (
          <div className="project-tags mb-2">
            {project.technologies.slice(0, 3).map((tech, index) => (
              <Badge key={index} bg="secondary" className="me-1 mb-1">
                {tech}
              </Badge>
            ))}
            {project.technologies.length > 3 && (
              <Badge bg="secondary" className="me-1 mb-1">
                +{project.technologies.length - 3}
              </Badge>
            )}
          </div>
        )}

        {project.sharing_platforms && project.sharing_platforms.length > 0 && (
          <div className="project-sharing mb-2">
            <small className="text-muted me-2">Share on:</small>
            {project.sharing_platforms.slice(0, 3).map((platform, index) => (
              <span key={index} className="sharing-icon me-1">
                {getSharingIcon(platform)}
              </span>
            ))}
          </div>
        )}

        {project.progress_percentage !== undefined && project.progress_percentage !== null && (
          <div className="project-progress mb-2">
            <small className="text-muted">Progress: {project.progress_percentage}%</small>
            <ProgressBar
              now={project.progress_percentage}
              variant={project.status === 'completed' ? 'success' : 'info'}
              className="mt-1"
            />
          </div>
        )}

        {/* Timer Display */}
        {timerActive && (
          <div className="project-timer mb-2">
            <Badge
              bg={timerPaused ? 'warning' : 'success'}
              className="d-flex align-items-center gap-1"
            >
              <FaClock />
              {formatTime(elapsedSeconds)}
              {timerPaused && ' (Paused)'}
            </Badge>
          </div>
        )}

        {/* Date Information */}
        <div className="project-dates mb-2">
          {project.created_at && (
            <small className="text-muted d-block">
              <i className="fa-solid fa-calendar-plus me-1" />
              Created: {formatDate(project.created_at)}
            </small>
          )}
          {project.started_at && (
            <small className="text-muted d-block">
              <i className="fa-solid fa-play-circle me-1" />
              Started: {formatDate(project.started_at)}
            </small>
          )}
          {project.completed_at && (
            <small className="text-muted d-block">
              <i className="fa-solid fa-check-circle me-1" />
              Completed: {formatDate(project.completed_at)}
            </small>
          )}
          {project.last_worked_at && !project.completed_at && (
            <small className="text-muted d-block">
              <i className="fa-solid fa-clock me-1" />
              Last worked: {formatDate(project.last_worked_at)}
            </small>
          )}
        </div>

        {project.estimated_hours && (
          <div className="project-meta mb-2">
            <small className="text-muted">
              <i className="fa-solid fa-hourglass-half me-1" />
              Est: {project.estimated_hours}h
              {elapsedSeconds > 0 && (
                <span className="ms-2">| Spent: {(elapsedSeconds / 3600).toFixed(1)}h</span>
              )}
            </small>
          </div>
        )}

        <div className="project-actions mt-3 d-flex gap-1 flex-wrap">
          {/* Timer Controls */}
          {!timerActive && project.status !== 'completed' && (
            <Button
              variant="success"
              size="sm"
              onClick={handleStartTimer}
              className="icon-only-btn"
              title="Start working"
              aria-label="Start working"
            >
              <FaPlay />
            </Button>
          )}
          {timerActive && !timerPaused && (
            <Button
              variant="warning"
              size="sm"
              onClick={handlePauseTimer}
              className="icon-only-btn"
              title="Pause"
              aria-label="Pause"
            >
              <FaPause />
            </Button>
          )}
          {timerActive && timerPaused && (
            <Button
              variant="success"
              size="sm"
              onClick={handleResumeTimer}
              className="icon-only-btn"
              title="Resume"
              aria-label="Resume"
            >
              <FaPlay />
            </Button>
          )}
          {timerActive && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleStopTimer}
              className="icon-only-btn"
              title="Stop working"
              aria-label="Stop working"
            >
              <FaStop />
            </Button>
          )}

          {/* Edit/Delete Controls */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(project);
            }}
            className="icon-only-btn"
            title="Edit project"
            aria-label="Edit project"
          >
            <FaEdit />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(project);
            }}
            className="icon-only-btn"
            title="Delete project"
            aria-label="Delete project"
          >
            <FaTrash />
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

ProjectCard.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    status: PropTypes.string.isRequired,
    progress_percentage: PropTypes.number,
    difficulty_level: PropTypes.string,
    estimated_hours: PropTypes.number,
    ai_generated: PropTypes.bool,
    technologies: PropTypes.arrayOf(PropTypes.string),
    sharing_platforms: PropTypes.arrayOf(PropTypes.string),
    timer_active: PropTypes.bool,
    timer_paused: PropTypes.bool,
    elapsed_seconds: PropTypes.number,
    created_at: PropTypes.string,
    started_at: PropTypes.string,
    completed_at: PropTypes.string,
    last_worked_at: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onTimerStart: PropTypes.func,
  onTimerPause: PropTypes.func,
  onTimerResume: PropTypes.func,
  onTimerStop: PropTypes.func,
};

export default ProjectCard;
